import { Fragment, useEffect, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { useMarketplace } from '../../../context/MarketplaceContext';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface MarketplaceOption {
  id: 'kiosk' | 'boutique' | 'restaurant';
  name: string;
  description: string;
  logo: string;
}

const storageBase = import.meta.env.VITE_SUPABASE_URL ? `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public` : '';
const marketplaceOptions: MarketplaceOption[] = [
  {
    id: 'kiosk',
    name: 'Kiosk',
    description: 'Votre point de vente digital',
    logo: `${storageBase}/Files/Logos/Kiosk.gif`
  },
  {
    id: 'boutique',
    name: 'Boutiques',
    description: 'Commandez auprès des commerces',
    logo: `${storageBase}/Files/Logos/Boutiques.png`
  },
  {
    id: 'restaurant',
    name: 'Restaurants',
    description: 'Commandez vos plats préférés',
    logo: `${storageBase}/Files/Logos/Restaurant.png`
  }
];

export function MarketplaceSelector() {
  const { marketplaceType, setMarketplaceType } = useMarketplace();
  const [logos, setLogos] = useState<Record<string, string>>({});
  const currentOption = marketplaceOptions.find(opt => opt.id === marketplaceType) || marketplaceOptions[0];

  useEffect(() => {
    async function loadLogos() {
      try {
        const { data } = await supabase
          .from('miniapps')
          .select('name, logo')
          .in('name', ['Kiosk', 'Boutiques', 'Restaurants']);

        if (data) {
          const logoMap: Record<string, string> = {};
          data.forEach((app) => {
            const key = app.name.toLowerCase();
            if (key === 'kiosk') logoMap['kiosk'] = app.logo;
            else if (key === 'boutiques') logoMap['boutique'] = app.logo;
            else if (key === 'restaurants') logoMap['restaurant'] = app.logo;
          });
          setLogos(logoMap);
        }
      } catch (error) {
        console.error('Error loading logos:', error);
      }
    }
    loadLogos();
  }, []);

  const getLogo = (id: string) => {
    return logos[id] || marketplaceOptions.find(opt => opt.id === id)?.logo || '';
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="group relative inline-flex items-center gap-2.5 rounded-xl bg-white/10 backdrop-blur-sm px-4 py-2.5 text-sm font-semibold text-white shadow-sm ring-1 ring-white/30 transition-all duration-200 hover:bg-white/20 hover:shadow-md hover:ring-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#ff0442]">
          <div className="relative h-6 w-6 flex-shrink-0 overflow-hidden rounded-lg">
            <img
              src={getLogo(marketplaceType)}
              alt={currentOption.name}
              className="h-full w-full object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
          <span className="hidden font-medium sm:inline">{currentOption.name}</span>
          <ChevronDownIcon className="h-4 w-4 text-white/80 transition-transform duration-200 group-hover:text-white ui-open:rotate-180" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95 translate-y-1"
        enterTo="transform opacity-100 scale-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="transform opacity-100 scale-100 translate-y-0"
        leaveTo="transform opacity-0 scale-95 translate-y-1"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 w-72 origin-top-right rounded-2xl bg-white/95 backdrop-blur-xl shadow-xl ring-1 ring-neutral-200/50 focus:outline-none dark:bg-neutral-800/95 dark:ring-neutral-700/50">
          <div className="p-2">
            {marketplaceOptions.map((option) => {
              const isSelected = marketplaceType === option.id;
              return (
                <Menu.Item key={option.id}>
                  {({ active }) => (
                    <button
                      onClick={() => setMarketplaceType(option.id)}
                      className={`group relative flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-left transition-all duration-200 ${
                        active
                          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
                          : ''
                      } ${
                        isSelected
                          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm ring-1 ring-blue-200/50 dark:from-blue-900/30 dark:to-indigo-900/30 dark:ring-blue-800/50'
                          : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                      }`}
                    >
                      <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-neutral-200/50 dark:bg-neutral-700 dark:ring-neutral-600/50">
                        <img
                          src={getLogo(option.id)}
                          alt={option.name}
                          className="h-full w-full object-contain p-1.5"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                      <div className="flex flex-1 flex-col gap-0.5">
                        <span className={`text-sm font-semibold ${
                          isSelected
                            ? 'text-blue-700 dark:text-blue-300'
                            : 'text-neutral-800 dark:text-neutral-100'
                        }`}>
                          {option.name}
                        </span>
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">
                          {option.description}
                        </span>
                      </div>
                      {isSelected && (
                        <div className="flex-shrink-0">
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 dark:bg-blue-500">
                            <CheckIcon className="h-4 w-4 text-white" />
                          </div>
                        </div>
                      )}
                    </button>
                  )}
                </Menu.Item>
              );
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

